import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PaletteIcon } from '@phosphor-icons/react/dist/csr/Palette';
import { UserCircleIcon } from '@phosphor-icons/react/dist/csr/UserCircle';
import { CardHead } from '../../design-system/composites/Card/Card';

import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { KeyIcon } from '@phosphor-icons/react/dist/csr/Key';
import { PasswordIcon } from '@phosphor-icons/react/dist/csr/Password';

import { AppearanceSettings } from './AppearanceSettings';
import { MyActivity } from './MyActivity';
import { SessionList } from './SessionList';
import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select } from '../../design-system/primitives/Select/Select';
import {
  normaliseAppearance,
  useAppearance,
  type Density,
  type Locale,
  type Theme,
} from '../../design-system/providers/appearance';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  api,
  type ApiUser,
} from '../../lib/api/client';
import styles from './SettingsScreen.module.css';

/**
 * A person's own name, language and appearance — `PATCH /api/v1/profile`.
 *
 * Deliberately NOT a username or an email field: task 7's `api.updateProfile`
 * accepts only `full_name`, `locale`, `theme` and `density`, and it accepts
 * them on purpose. Email is the account-recovery channel and the API refuses
 * it here; a field this screen never sends is a field it never has to explain
 * why it silently ignored.
 *
 * ## The running application has to follow the save, not just the record
 *
 * `theme`, `density` and `locale` are server-authoritative (decision S-5,
 * `session.ts`'s docblock) — Task 7 already applies them the moment a sign-in
 * or a reload learns what the server has on file. This screen is the other
 * moment they can change: while the account is already sitting on the page.
 * `useAppearance()`'s setters are the ONLY writer for `data-theme`,
 * `data-density`, `lang` and `dir` (`appearance.ts`'s docblock), and
 * `I18nProvider` already subscribes to `locale` and calls
 * `i18n.changeLanguage` whenever it changes — so calling those setters with
 * the server's own response is what makes a saved preference visible in the
 * SAME frame the confirmation appears in, rather than only after the next
 * sign-in. `normaliseAppearance` is the same coercion `session.ts` runs on the
 * identical three fields, so a value the server sent that this client does not
 * recognise falls back exactly the way an untrusted stored value already does,
 * rather than being fed to `setAttribute` unchecked.
 *
 * ## 422 renders the server's own words; anything else is one generic alert
 *
 * Part 2a exists because two endpoints were once answering a hardcoded English
 * sentence into an Arabic interface. `error.details` is already in the
 * account's language (the API renders it there), so a validation failure is
 * shown VERBATIM, field by field — never replaced by a string this client
 * invented. Every other failure — a permission refusal, a throttle, an
 * expired session, an unreachable API, anything unclassified — has no
 * meaningful field to attach to and renders as the one `role="alert"` region,
 * with the fields left exactly as the person typed them. A save that failed
 * and then cleared what they typed would cost them the retry it just told them
 * to make.
 */

export interface SettingsScreenProps {
  user: ApiUser;
  /**
   * Called with the server's response after a successful save.
   *
   * `App.tsx` seeds this screen from `session.user`, which nothing else in
   * this component's lifetime refreshes — the screen's own `state` is reset
   * from `user` exactly once, in `useState`'s initialiser. Without this
   * callback a save that changed `theme` is invisible to `session.user`:
   * navigating away and back reads `stateFor(session.user)` again, the select
   * reverts to whatever the account held at sign-in, and the NEXT unrelated
   * save (renaming yourself, say) resends that stale value and silently
   * overwrites the one already on the server. `useSession()`'s `signIn`
   * already does exactly what is needed here — `applyServerAppearance(user)`
   * plus `setSession({status:'authenticated', user})` — so `App.tsx` passes
   * it straight through; this prop exists so the component does not have to
   * import `useSession` itself. Optional in TYPE only (matching
   * `MfaEnrolmentScreen.tsx`'s `onEnrolled`/`onDisabled`), not in practice.
   */
  onSaved?: (user: ApiUser) => void;
  /**
   * Reaches `ChangePasswordScreen`'s VOLUNTARY path (task 9's plan
   * responsibility for that file: "Voluntary and forced"). `App.tsx` wires
   * this to `setActivePath('/password')` rather than this screen owning any
   * routing of its own — the same separation `onSaved` already keeps: this
   * component describes an intent, the shell decides what that means.
   */
  onChangePassword?: () => void;
}

interface FormState {
  fullName: string;
  locale: Locale;
  theme: Theme;
  density: Density;
}

function stateFor(user: ApiUser): FormState {
  const appearance = normaliseAppearance({
    theme: user.theme,
    density: user.density,
    locale: user.locale,
  });
  return { fullName: user.full_name, ...appearance };
}

/** `ApiExceptionRenderer::STATUSES` — not exported from `client.ts` as a constant,
 *  same trap `UserFormDialog.tsx` and `UserActions.tsx` already document. */
const FORBIDDEN_CODE = 'forbidden';

/** First message per field, matching `UserFormDialog.tsx`'s `mapDetails`. */
function mapDetails(details: Readonly<Record<string, string[]>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, messages] of Object.entries(details)) {
    const [first] = messages;
    if (first !== undefined) out[field] = first;
  }
  return out;
}

export function SettingsScreen({ user, onSaved, onChangePassword }: SettingsScreenProps) {
  const { t } = useTranslation();
  const appearance = useAppearance();

  const [state, setState] = useState<FormState>(() => stateFor(user));
  const [missing, setMissing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /** Read inside the handler: two clicks in one React batch both see `false`. */
  const inFlight = useRef(false);
  const fullNameRef = useRef<HTMLInputElement>(null);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('settings.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('auth.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('settings.saveFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement: everything below can throw, and a default-action submit
    // on a form with no method is a GET that would put every field in the URL.
    event.preventDefault();

    if (inFlight.current) return;

    const trimmedName = state.fullName.trim();
    if (trimmedName === '') {
      setMissing(true);
      return;
    }
    setMissing(false);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);
    setSaved(false);

    try {
      const updated = await api.updateProfile({
        full_name: trimmedName,
        locale: state.locale,
        theme: state.theme,
        density: state.density,
      });

      // The server is authoritative (decision S-5) — reflected into the
      // running app immediately, not only at the next sign-in. See the
      // component docblock.
      const next = normaliseAppearance({
        theme: updated.theme,
        density: updated.density,
        locale: updated.locale,
      });
      appearance.setTheme(next.theme);
      appearance.setDensity(next.density);
      appearance.setLocale(next.locale);

      setState({ fullName: updated.full_name, ...next });
      setSaved(true);
      // See the prop docblock: without this, `session.user` (App.tsx's copy)
      // goes stale the instant this screen unmounts, and the NEXT save reads
      // it back and resends the value the account had at sign-in.
      onSaved?.(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        // A fault in our own code. The agent must be told the save did
        // nothing, and the fault must not be disguised as a validation
        // problem.
        setGeneralFailure(t('settings.saveFailed'));
        throw error;
      }

      // Kept open, typing intact: nothing on this path touches `state`.
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div className={styles['page']}>
      {/*
        Two columns that stack independently: account and security on one
        side, appearance on the other. A grid of rows made every row as tall
        as its tallest panel, which left a hole under the short account form.
      */}
      <div className={styles['columns']}>
        <div className={styles['column']}>
          <section className={[styles['panel'], styles['primary']].join(' ')} aria-labelledby="settingsTitle">
            <CardHead id="settingsTitle" icon={<UserCircleIcon size={16} weight="duotone" />} title={t('settings.title')} />

            {/*
              Both live regions mounted from the first frame, EMPTY — assistive
              technology announces a change inside a region it is already
              watching. Same shape as `MfaEnrolmentScreen.tsx`.
            */}
            <div className={styles['notice']} role="status" aria-atomic="true">
              {saved ? t('settings.saved') : null}
            </div>

            <div className={styles['alert']} role="alert" aria-atomic="true">
              {generalFailure}
            </div>

            <form
              className={styles['form']}
              method="post"
              noValidate
              onSubmit={(event) => {
                void handleSubmit(event);
              }}
            >
              <Input
                ref={fullNameRef}
                className={styles['wide']}
                label={t('settings.fieldFullName')}
                width="fill"
                required
                value={state.fullName}
                error={missing ? t('settings.required') : fieldErrors['full_name']}
                onChange={(event) => {
                  const { value } = event.target;
                  setState((current) => ({ ...current, fullName: value }));
                }}
              />

              <Select
                label={t('settings.fieldLocale')}
                width="compact"
                required
                options={[
                  { value: 'en', label: t('locale.english') },
                  { value: 'ar', label: t('locale.arabic') },
                ]}
                value={state.locale}
                error={fieldErrors['locale']}
                onChange={(event) => {
                  const value = event.target.value as Locale;
                  setState((current) => ({ ...current, locale: value }));
                }}
              />

              <Select
                label={t('settings.fieldTheme')}
                width="compact"
                required
                options={[
                  { value: 'dark', label: t('theme.dark') },
                  { value: 'light', label: t('theme.light') },
                ]}
                value={state.theme}
                error={fieldErrors['theme']}
                onChange={(event) => {
                  const value = event.target.value as Theme;
                  setState((current) => ({ ...current, theme: value }));
                }}
              />

              <Select
                label={t('settings.fieldDensity')}
                width="compact"
                required
                options={[
                  { value: 'comfortable', label: t('density.comfortable') },
                  { value: 'compact', label: t('density.compact') },
                ]}
                value={state.density}
                error={fieldErrors['density']}
                onChange={(event) => {
                  const value = event.target.value as Density;
                  setState((current) => ({ ...current, density: value }));
                }}
              />

              <div className={styles['formActions']}>
                <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
                  {t('settings.save')}
                </Button>
              </div>
            </form>
          </section>

          {/*
            A second, separate panel — not another field in the form above. A
            password change is its own screen with its own confirmation and its
            own server errors (`ChangePasswordScreen`); folding a fourth control
            in here would either submit it silently alongside a name change or
            need a second submit button inside one `<form>`, neither of which the
            handoff's one-primary-action-per-region rule allows.
          */}
          <section className={styles['panel']} aria-labelledby="settingsSecurityTitle">
            <CardHead id="settingsSecurityTitle" icon={<KeyIcon size={16} weight="duotone" />} title={t('settings.security')} />

            <div className={styles['actions']}>
              <Button
                type="button"
                variant="secondary"
                icon={<PasswordIcon size={16} />}
                onClick={() => {
                  onChangePassword?.();
                }}
              >
                {t('settings.changePassword')}
              </Button>
            </div>
          </section>

          {/*
            Task 10: the devices signed in to this account. A self-contained
            panel — it fetches `api.sessions()` itself and owns its own load
            status, so this screen hands it nothing and reads nothing back.
          */}
        </div>
        <div className={styles['column']}>
          {/*
            Accent and background — this browser's preferences, applied as they
            are picked (see `AppearanceSettings`), so they sit in their own panel
            rather than in the account form whose Save writes to the server.
          */}
          <section className={styles['panel']} aria-labelledby="settingsAppearanceTitle">
            <CardHead
              id="settingsAppearanceTitle"
              icon={<PaletteIcon size={16} weight="duotone" />}
              title={t('settings.appearance')}
            />
            <AppearanceSettings />
          </section>
        </div>
      </div>

      <div className={styles['fullRow']}>
        <SessionList />
      </div>

      {/*
        Task 8: the caller's own audit trail, reusing `AuditEntryTable`
        (task 7) unchanged. Self-contained the same way `SessionList` is —
        it fetches `api.myActivity()` itself and owns its own load status and
        cursor, so this screen hands it nothing and reads nothing back.
      */}
      <div className={styles['fullRow']}>
        <MyActivity />
      </div>
    </div>
  );
}
